import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Badge } from '../ui/Badge';
import { useReports } from '../../hooks/useReports';
import { useProjects } from '../../hooks/useProjects';
import { 
  X, 
  FileText, 
  Calendar, 
  Users, 
  BarChart3, 
  TrendingUp, 
  Target,
  Globe,
  Mail,
  Smartphone,
  ShoppingCart,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Loader,
  Clock,
  Star,
  Eye,
  Settings,
  Play,
  Download,
  Edit3,
  Zap,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle
} from 'lucide-react';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  widgets: string[];
  estimatedTime: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  popular: boolean;
  icon: React.ComponentType<any> | string;
  preview: string;
  comingSoon: boolean;
}

interface TemplatePreviewModalProps {
  isOpen: boolean;
  template: ReportTemplate | null;
  onClose: () => void;
  onCreateFromTemplate: (template: ReportTemplate, customization: any) => void;
}

export function TemplatePreviewModal({ 
  isOpen, 
  template, 
  onClose, 
  onCreateFromTemplate 
}: TemplatePreviewModalProps) {
  const [step, setStep] = useState<'preview' | 'customize' | 'creating' | 'success'>('preview');
  const [isCreating, setIsCreating] = useState(false);
  const [customization, setCustomization] = useState({
    name: '',
    description: '',
    schedule: 'none',
    dateRange: {
      type: 'last_30_days' as 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom',
      start: '',
      end: ''
    },
    widgets: [] as string[],
    projectId: ''
  });

  const { createReport, generateReport } = useReports();
  const { projects, isLoading: projectsLoading } = useProjects();

  React.useEffect(() => {
    if (template && isOpen) {
      setCustomization({
        name: template.name || '',
        description: template.description || '',
        schedule: 'none',
        dateRange: {
          type: 'last_30_days',
          start: '',
          end: ''
        },
        widgets: [...(template.widgets || [])],
        projectId: projects.length > 0 ? projects[0].id : ''
      });
      setStep('preview');
    }
  }, [template, isOpen, projects]);

  // Don't render if template is coming soon
  if (!isOpen || !template || template.comingSoon) {
    return null;
  }

  const handleCreateReport = async () => {
    if (!template) return;
    
    // Validation
    if (!customization.projectId) {
      alert('Please select a project for the report');
      return;
    }
    
    setStep('creating');
    setIsCreating(true);
    
    try {
      // Map template categories to valid reportTypes
      const getReportType = (templateId: string, category: string) => {
        const mapping: Record<string, string> = {
          'marketing-overview': 'analytics',
          'traffic-analysis': 'analytics', 
          'conversion-funnel': 'conversion',
          'social-media': 'social',
          'ecommerce-sales': 'analytics',
          'email-marketing': 'analytics',
          'mobile-app': 'analytics',
          'competitor-analysis': 'analytics'
        };
        return mapping[templateId] || 'custom';
      };

      const reportData = {
        name: customization.name,
        description: customization.description,
        projectId: customization.projectId,
        reportType: getReportType(template.id, template.category),
        config: {
          // Required fields for backend validation
          dateRange: customization.dateRange,
          metrics: [
            'sessions',
            'users',
            'pageviews',
            'bounceRate',
            'sessionDuration',
            'conversionRate'
          ],
          includeInsights: true,
          // Template-specific fields (convert string widgets to proper objects)
          widgets: customization.widgets.map((widget, index) => ({
            id: `${widget.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')}-${index}`,
            type: index % 3 === 0 ? 'chart' : index % 3 === 1 ? 'metric' : 'table',
            title: widget,
            dataSource: 'google_analytics'
          })),
          template: template.id,
          templateName: template.name,
          templateCategory: template.category
        },
        schedule: customization.schedule !== 'none' ? customization.schedule : null,
        isScheduled: customization.schedule !== 'none'
      };

      const result = await createReport(reportData);
      console.log('Template report created successfully:', result);
      
      // Automatically generate the report after creation
      if (result.report?.id) {
        console.log('Generating report automatically...');
        await generateReport(result.report.id);
        console.log('Report generated successfully');
      }
      
      // Show success state
      setStep('success');
      
      // Close modal after showing success
      setTimeout(() => {
        setStep('preview');
        setIsCreating(false);
        onClose();
        // Reset customization for next use
        setCustomization({
          name: '',
          description: '',
          schedule: 'none',
          dateRange: {
            type: 'last_30_days',
            start: '',
            end: ''
          },
          widgets: [],
          projectId: ''
        });
      }, 2000);
      
    } catch (error: any) {
      console.error('Failed to create report:', error);
      setIsCreating(false);
      setStep('customize');
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.details 
        ? `Validation failed: ${error.response.data.details.map((d: any) => d.msg).join(', ')}`
        : error.response?.data?.message || 'Failed to create report';
      
      alert(errorMessage);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800 border-green-200';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Advanced': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getIconComponent = (iconName: string | React.ComponentType<any>) => {
    // If it's already a React component, return it
    if (typeof iconName === 'function') {
      return iconName;
    }
    
    // Map string icon names from backend to React components
    const iconMap: Record<string, React.ComponentType<any>> = {
      // Backend icon names to React components
      'chart': TrendingUp,
      'speedometer': BarChart3,
      'target': Target,
      'users': Users,
      'share': Users,
      'cog': Target,
      'search': Globe,
      'analytics': BarChart3,
      'performance': BarChart3,
      'conversion': Target,
      'audience': Users,
      'seo': Globe,
      'social': Users,
      'custom': FileText,
      // Original frontend mappings
      'TrendingUp': TrendingUp,
      'Globe': Globe,
      'Target': Target,
      'Users': Users,
      'ShoppingCart': ShoppingCart,
      'Mail': Mail,
      'Smartphone': Smartphone,
      'BarChart3': BarChart3
    };
    return iconMap[iconName] || FileText;
  };

  const getWidgetIcon = (widget: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      'Traffic Sources': Globe,
      'Conversion Rates': Target,
      'Campaign Performance': TrendingUp,
      'ROI Analysis': BarChart3,
      'Page Views': FileText,
      'Unique Visitors': Users,
      'Bounce Rate': TrendingUp,
      'Session Duration': Clock,
      'Engagement Rate': Users,
      'Follower Growth': TrendingUp,
      'Post Performance': Star,
      'Social ROI': BarChart3,
      'Revenue Trends': TrendingUp,
      'Product Performance': ShoppingCart,
      'Customer Segments': Users,
      'Cart Analysis': ShoppingCart,
      'Open Rates': Mail,
      'Click Rates': Target,
      'Unsubscribe Rate': TrendingUp,
      'Revenue per Email': BarChart3,
      'App Downloads': Smartphone,
      'User Retention': Users,
      'In-App Events': Zap,
      'Revenue per User': BarChart3,
      'Market Share': BarChart3,
      'Keyword Rankings': TrendingUp,
      'Social Presence': Users,
      'Content Analysis': FileText
    };
    return iconMap[widget] || FileText;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => setStep('preview')}
              className={`${step === 'preview' ? 'hidden' : ''} text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200`}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {step === 'preview' && 'Template Preview'}
                {step === 'customize' && 'Customize Template'}
                {step === 'creating' && 'Creating Report'}
                {step === 'success' && 'Report Created!'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {template?.name}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Preview Step */}
          {step === 'preview' && template && (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Template Info */}
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-100 dark:from-blue-900/30 to-purple-100 dark:to-purple-900/30 rounded-lg flex items-center justify-center">
                        {React.createElement(getIconComponent(template.icon), { 
                          className: "w-6 h-6 text-blue-600 dark:text-blue-400" 
                        })}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{template.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {template.category}
                          </Badge>
                          {template.popular && (
                            <Badge variant="secondary" className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                              <Star className="w-3 h-3 mr-1" />
                              Popular
                            </Badge>
                          )}
                          <Badge 
                            variant="secondary" 
                            className={`${getDifficultyColor(template.difficulty)} border-0`}
                          >
                            {template.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {template.description}
                    </p>
                  </div>

                  {/* Included Widgets */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Included Widgets ({template.widgets?.length || 0})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {template.widgets?.map((widget, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                          {React.createElement(getWidgetIcon(widget), { 
                            className: "w-5 h-5 text-blue-600 dark:text-blue-400" 
                          })}
                          <span className="font-medium text-gray-900 dark:text-white">{widget}</span>
                        </div>
                      )) || (
                        <div className="text-gray-500 dark:text-gray-400 italic">No widgets specified</div>
                      )}
                    </div>
                  </div>

                  {/* Preview */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Preview</h4>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                      <div className="aspect-video bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-purple-50 dark:to-purple-900/20 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                        <div className="text-center">
                          <Eye className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                          <p className="text-gray-600 dark:text-gray-400 font-medium">Report Preview</p>
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                            This template will generate a comprehensive {template.category} report
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <Card className="p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Template Details</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Widgets</span>
                        <span className="font-medium text-gray-900 dark:text-white">{template.widgets?.length || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Setup Time</span>
                        <span className="font-medium text-gray-900 dark:text-white">{template.estimatedTime}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Difficulty</span>
                        <Badge 
                          variant="secondary" 
                          className={`${getDifficultyColor(template.difficulty)} border-0 text-xs`}
                        >
                          {template.difficulty}
                        </Badge>
                      </div>
                    </div>
                  </Card>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button 
                      onClick={() => setStep('customize')}
                      className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Use This Template
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      onClick={onClose}
                      className="w-full border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Browse More Templates
                    </Button>
                  </div>

                  {/* Requirements */}
                  <Card className="p-4 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700">
                    <h4 className="font-semibold text-orange-900 dark:text-orange-300 mb-2 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Requirements
                    </h4>
                    <ul className="text-sm text-orange-800 dark:text-orange-300 space-y-1">
                      <li>• Google Analytics connection required</li>
                      <li>• Minimum 30 days of data recommended</li>
                      <li>• Goal tracking setup preferred</li>
                    </ul>
                  </Card>
                </div>
              </div>
            </div>
          )}

          {/* Customize Step */}
          {step === 'customize' && template && (
            <div className="p-6">
              <div className="max-w-2xl mx-auto space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Customize Your {template.name} Report
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Configure the basic settings for your new report
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Project Selection */}
                  {!projectsLoading && projects.length > 0 && (
                    <div>
                      <Label htmlFor="project" className="text-gray-700 dark:text-gray-300">Project</Label>
                      <select
                        id="project"
                        value={customization.projectId}
                        onChange={(e) => setCustomization(prev => ({ ...prev, projectId: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select a project</option>
                        {projects.map((project: any) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Report Name */}
                  <div>
                    <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">Report Name</Label>
                    <Input
                      id="name"
                      value={customization.name}
                      onChange={(e) => setCustomization(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter a name for your report"
                      className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <Label htmlFor="description" className="text-gray-700 dark:text-gray-300">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={customization.description}
                      onChange={(e) => setCustomization(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what this report will track"
                      className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>

                  {/* Date Range */}
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Date Range</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                      {[
                        { value: 'last_7_days', label: 'Last 7 Days' },
                        { value: 'last_30_days', label: 'Last 30 Days' },
                        { value: 'last_90_days', label: 'Last 90 Days' },
                        { value: 'custom', label: 'Custom Range' }
                      ].map((range) => (
                        <div
                          key={range.value}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            customization.dateRange.type === range.value
                              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                          }`}
                          onClick={() => setCustomization(prev => ({ 
                            ...prev, 
                            dateRange: { ...prev.dateRange, type: range.value as any } 
                          }))}
                        >
                          <div className="text-center">
                            <Calendar className={`w-4 h-4 mx-auto mb-1 ${
                              customization.dateRange.type === range.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                            }`} />
                            <span className={`text-xs font-medium ${
                              customization.dateRange.type === range.value ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                            }`}>
                              {range.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Schedule */}
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Schedule (Optional)</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                      {[
                        { value: 'none', label: 'One-time' },
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' }
                      ].map((schedule) => (
                        <div
                          key={schedule.value}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            customization.schedule === schedule.value
                              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                          }`}
                          onClick={() => setCustomization(prev => ({ ...prev, schedule: schedule.value }))}
                        >
                          <div className="text-center">
                            <Clock className={`w-4 h-4 mx-auto mb-1 ${
                              customization.schedule === schedule.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                            }`} />
                            <span className={`text-xs font-medium ${
                              customization.schedule === schedule.value ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                            }`}>
                              {schedule.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <Card className="p-4 bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Report Summary</h4>
                  <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <p><span className="font-medium">Template:</span> {template.name}</p>
                    <p><span className="font-medium">Widgets:</span> {template.widgets?.length || 0} included</p>
                    <p><span className="font-medium">Schedule:</span> {customization.schedule === 'none' ? 'One-time report' : `${customization.schedule} updates`}</p>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Creating Step */}
          {step === 'creating' && (
            <div className="p-6">
              <div className="max-w-md mx-auto text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Creating Your Report
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Please wait while we set up your report with the selected template...
                </p>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="p-6">
              <div className="max-w-md mx-auto text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Report Created Successfully!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Your {template?.name} report has been created and is being generated.
                </p>
                <Button 
                  onClick={onClose}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  View Reports
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'customize' && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
            <Button 
              variant="outline" 
              onClick={() => setStep('preview')}
              className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Preview
            </Button>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateReport}
                disabled={isCreating || !customization.projectId || !customization.name}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500 dark:hover:from-blue-600 dark:hover:to-purple-600 text-white"
              >
                {isCreating ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Create Report
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
} 